import axios from "axios";
import { HttpClient, HttpMethods, RequestConfig } from "@planzone/services";
import { serialize } from "object-to-formdata";
import type { File } from "@planzone/dto";

function sendRequest<Response>(config: RequestConfig): Promise<Response> {
  const requestData =
    config.method === HttpMethods.GET
      ? { params: config.data }
      : { data: config.data };

  return new Promise<Response>((resolve, reject) =>
    axios({
      url: config.baseUrl + config.endpoint,
      method: config.method,
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache=no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
        ...config.headers,
      },
      ...requestData,
    })
      .then(({ data }) => resolve(data))
      .catch((error) =>
        reject(
          !!error.response
            ? error.response.data
            : !!error.request
              ? error.request
              : error,
        ),
      ),
  );
}

function fileToBlob(file: File): Promise<Blob> {
  return axios
    .get(file.uri, { responseType: "blob" })
    .then(({ data }) => new File([data], file.name, { type: file.type }));
}

function convertDataToFormData(
  data: Object,
  files: { [key: string]: File },
): Promise<FormData> {
  return Promise.all(
    Object.entries(files).map(([key, file]) =>
      fileToBlob(file).then((blob) => [key, blob] as [string, Blob]),
    ),
  )
    .then((f) =>
      f.reduce((nextFiles, [key, file]) => ({ ...nextFiles, [key]: file }), {}),
    )
    .then((nextFiles) =>
      serialize(
        { ...nextFiles, ...data },
        {
          indices: true,
          nullsAsUndefineds: false,
          booleansAsIntegers: false,
          allowEmptyArrays: true,
        },
      ),
    );
}

function uploadData<Response>(config: RequestConfig): Promise<Response> {
  return new Promise<Response>((resolve, reject) =>
    convertDataToFormData(config.data || {}, config.files || {}).then(
      (formData) =>
        axios({
          url: config.baseUrl + config.endpoint,
          method: config.method,
          headers: {
            "Content-Type": "multipart/form-data",
            ...config.headers,
          },
          data: formData,
        })
          .then(({ data }) => resolve(data))
          .catch((error) =>
            reject(
              !!error.response
                ? error.response.data
                : !!error.request
                  ? error.request
                  : error,
            ),
          ),
    ),
  );
}

function hasFiles(files?: object) {
  return files && Object.keys(files).length > 0;
}

function cleanHeaders(headers: { [p: string]: string } | undefined) {
  return headers
    ? Object.entries(headers)
        .filter(([_, value]) => !!value)
        .reduce(
          (nextHeaders, [key, value]) => ({ ...nextHeaders, [key]: value }),
          {},
        )
    : undefined;
}

function createHttpClient(): HttpClient {
  return {
    sendHttpRequest<Response>(config: RequestConfig): Promise<Response> {
      return (hasFiles(config.files) ? uploadData : sendRequest)({
        ...config,
        headers: cleanHeaders(config.headers),
      });
    },
  };
}

export { createHttpClient };
